import { AgGridAngular } from '@ag-grid-community/angular';
import { GridApi, GridReadyEvent } from '@ag-grid-community/core';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  HostListener,
  inject,
  Input,
  input,
  signal,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import dayjs, { Dayjs } from 'dayjs';
import { isEqual } from 'lodash-es';
import { BehaviorSubject, combineLatest, firstValueFrom } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { getAttributeColumns } from '@features/budget-attributes/services/budget-attributes.service';
import { ExportExcelButtonComponent } from '@features/export-excel-button/export-excel-button.component';
import { InvoiceWorkflowDirective } from '@features/feature-workflow/directives/invoice-workflow.directive';

import { ExtendedBudgetData } from '@models/budget/budget.model';

import { ButtonComponent } from '@shared/components/button/button.component';
import { InputComponent } from '@shared/components/input/input.component';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { GridDynamicHeightDirective } from '@shared/directives/grid-dynamic-height.directive';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { ChangeOrderSharedService } from '@shared/services/change-order-shared.service';
import {
  BudgetActivityAttributes,
  BudgetType,
  EntityType,
  EventType,
  GqlService,
  listBudgetActivitiesQuery,
  listInvoiceActivityMappingsQuery,
  listUserNamesWithEmailQuery,
  NoteType,
  PermissionType,
  WorkflowStep,
} from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { AuthService } from '@shared/store/auth/auth.service';
import { EventService } from '@shared/store/event/event.service';
import { InvoiceModel } from '@shared/store/invoice/invoice.model';
import { MainQuery } from '@shared/store/main/main.query';
import { WorkflowQuery } from '@shared/store/workflow/workflow.query';
import { decimalAdd } from '@shared/utils';
import { ExportType, Utils } from '@shared/utils/utils';

import { MapActivitiesModalComponent } from './map-activities-modal/map-activities-modal.component';
import {
  MapActivitiesModalParams,
  MapActivitiesModalReturnData,
  MapActivitiesTotals,
} from './map-activities-modal/map-activities-modal.model';
import { MapActivitiesNoRowsComponent } from './map-activities-no-rows/map-activities-no-rows.component';
import { MapActivitiesTabGridRow } from './map-activities-tab.model';
import { getMapActivitiesTabGridOptions } from './map-activities.constans';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [InvoiceWorkflowDirective],
  imports: [
    MapActivitiesNoRowsComponent,
    AgGridAngular,
    GridDynamicHeightDirective,
    ButtonComponent,
    InputComponent,
    ReactiveFormsModule,
    ExportExcelButtonComponent,
    TooltipDirective,
  ],
  selector: 'aux-map-activities-tab',
  templateUrl: 'map-activities-tab.component.html',
})
export class MapActivitiesTabComponent {
  authService = inject(AuthService);
  changeOrderSharedService = inject(ChangeOrderSharedService);
  eventService = inject(EventService);
  invoiceCtx = inject(InvoiceWorkflowDirective);
  mainQuery = inject(MainQuery);
  workflowQuery = inject(WorkflowQuery);

  private gqlService = inject(GqlService);
  private overlayService = inject(OverlayService);

  activitiesSaving = signal(false);
  attributes: WritableSignal<BudgetActivityAttributes[]> = signal([]);
  budgetActivityOptions: WritableSignal<listBudgetActivitiesQuery[]> = signal([]);
  loading = signal(true);
  notesSaving = signal(false);

  btnTooltip = computed(() => {
    if (!this.hasEditPermission()) {
      return MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION;
    }

    if (this.isInvoiceFinalized()) {
      return this.invoiceLockTooltip();
    }

    return '';
  });

  invoice = input.required<InvoiceModel>();

  gridData: MapActivitiesTabGridRow[] = [];

  gridOptions = computed(() => {
    const invoice = this.invoice();
    const attributes = this.attributes();
    const budgetActivityOptions = this.budgetActivityOptions();

    return getMapActivitiesTabGridOptions(
      invoice.organization.currency,
      attributes,
      budgetActivityOptions,
      this.gridData
    );
  });

  btnLoading$ = new BehaviorSubject<'export' | false>(false);
  budgetData: ExtendedBudgetData[] = [];
  gridAPI!: GridApi;
  hasEditPermission = this.authService.$isAuthorized({
    permissions: [PermissionType.PERMISSION_UPDATE_INVOICES],
  });
  @Input() invoiceAllocatedTotals$!: BehaviorSubject<MapActivitiesTotals>;
  invoiceLockTooltip = this.invoiceCtx.invoiceLockTooltip;
  isInvoiceFinalized = this.workflowQuery.getLockStatusByWorkflowStepType(
    WorkflowStep.WF_STEP_MONTH_CLOSE_LOCK_INVOICES
  );
  lastUpdatedList: Dayjs[] = [];
  lastUpdatedListWithUsers: { date: Dayjs; user: string }[] = [];
  noteControl = new FormControl({ disabled: true, value: '' });
  noteId = '';

  private calculatePinnedBottomData(): {
    amount: number;
  } {
    const columnsWithAggregation = ['amount'] as const;

    this.gridAPI.selectAll();

    return this.gridAPI.getSelectedRows().reduce(
      (acc, row) => {
        for (const key of columnsWithAggregation) {
          const currentVal = acc[key];
          const additionalVal = row[key];

          if (Utils.isNumber(currentVal) && Utils.isNumber(additionalVal)) {
            acc[key] = decimalAdd(currentVal, additionalVal);
          }
        }

        return acc;
      },
      {
        amount: 0,
      }
    );
  }

  constructor() {
    toObservable(this.invoice)
      .pipe(
        switchMap((invoice) => {
          return combineLatest([
            this.gqlService.listInvoiceActivityMappings$({ invoice_id: invoice.id || '' }),
            this.gqlService.listNotes$({
              entity_id: invoice.id || '',
              entity_type: EntityType.INVOICE_ACTIVITY_MAPPING,
            }),
            this.gqlService.listBudgetActivities$({
              budget_type: BudgetType.BUDGET_PRIMARY,
              vendor_id: invoice.organization.id,
            }),
          ]);
        })
      )
      .subscribe(
        ([
          listInvoiceActivityMappingsResponse,
          listNotesResponse,
          listBudgetActivitiesResponse,
        ]) => {
          if (
            listInvoiceActivityMappingsResponse.success &&
            listInvoiceActivityMappingsResponse.data
          ) {
            this.implementActivitiesData(listInvoiceActivityMappingsResponse.data);
          }

          if (listNotesResponse.success && listNotesResponse.data?.length) {
            this.lastUpdatedList.push(dayjs(listNotesResponse.data[0].update_date));
            this.lastUpdatedListWithUsers.push({
              date: dayjs(listNotesResponse.data[0].update_date),
              user: listNotesResponse.data[0].updated_by,
            });
            this.noteId = listNotesResponse.data[0].id;
            this.noteControl.setValue(listNotesResponse.data[0].message || '');
          }

          if (listBudgetActivitiesResponse.success && listBudgetActivitiesResponse.data) {
            this.budgetActivityOptions.set(listBudgetActivitiesResponse.data || []);

            if (this.budgetActivityOptions().length > 0) {
              this.attributes.set(
                getAttributeColumns({
                  attributes: this.budgetActivityOptions().map((z) => z.attributes || []),
                })
              );
            }
          }

          this.loading.set(false);
        }
      );

    this.mainQuery
      .select('userList')
      .pipe(takeUntilDestroyed())
      .subscribe((_users) => {
        _users.forEach((user: listUserNamesWithEmailQuery) => {
          this.changeOrderSharedService.users.set(user.sub, user);
        });
      });
  }

  private getNotes(): void {
    this.loading.set(true);
    this.gqlService
      .listNotes$({
        entity_id: this.invoice().id || '',
        entity_type: EntityType.INVOICE_ACTIVITY_MAPPING,
      })
      .subscribe((result) => {
        if (result.success && result.data?.length) {
          this.lastUpdatedList.push(dayjs(result.data[0].update_date));
          this.lastUpdatedListWithUsers.push({
            date: dayjs(result.data[0].update_date),
            user: result.data[0].updated_by,
          });
          this.noteId = result.data[0].id;
          this.noteControl.setValue(result.data[0].message || '');
        } else {
          this.noteId = '';
          this.noteControl.setValue('');
        }

        this.loading.set(false);
      });
  }

  implementActivitiesData(data: listInvoiceActivityMappingsQuery[]): void {
    const calculatedTotals: MapActivitiesTotals = {
      ACTIVITY_DISCOUNT: 0,
      ACTIVITY_INVESTIGATOR: 0,
      ACTIVITY_PASSTHROUGH: 0,
      ACTIVITY_SERVICE: 0,
      invoice_total: 0,
    };

    this.gridData = data
      ? data.map((item) => {
          if (item.activity_type) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            calculatedTotals[item.activity_type] =
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              calculatedTotals[item.activity_type] + item.amount;
          }

          this.lastUpdatedList.push(dayjs(item.update_date));
          this.lastUpdatedListWithUsers.push({
            date: dayjs(item.update_date),
            user: item.updated_by,
          });

          return {
            activity_id: item.activity_id,
            activity_name: item.activity_name,
            activity_type: item.activity_type,
            amount: item.amount,
            display_order: item.display_order,
            id: item.id,
            invoice_item_description: item.invoice_item_description || '',
          };
        })
      : [];
    this.invoiceAllocatedTotals$.next(calculatedTotals);
  }

  lastUpdated(): string {
    if (this.lastUpdatedList.length > 0) {
      const maxDate = dayjs.max(this.lastUpdatedList);
      const userId = this.lastUpdatedListWithUsers.find((user) =>
        isEqual(user.date, maxDate)
      )?.user;
      const userName = this.changeOrderSharedService.userFormatter(userId);

      return dayjs(maxDate)?.format('MMMM DD, YYYY [at] HH:mm') + ` by ${userName}` || '';
    }

    return '';
  }

  async onExport() {
    const trialShortName = this.mainQuery.getSelectedTrial()?.short_name || '';

    const result = await firstValueFrom(
      this.eventService.processEvent$({
        entity_id: this.invoice().id,
        entity_type: EntityType.TRIAL,
        payload: JSON.stringify({
          export_entity_id: this.invoice().id || '',
          export_type: ExportType.INVOICE_ACTIVITY_MAPPINGS,
          filename: `auxilius_invoice-mapping-${trialShortName}_${this.invoice().invoice_no}_${dayjs().format(
            'YYYY.MM.DD-HHmmss'
          )}`,
        }),
        type: EventType.GENERATE_EXPORT,
      })
    );

    if (result.success) {
      this.overlayService.success(
        'Export is being generated and will download when complete. You may leave the page.'
      );
    } else {
      this.overlayService.error(result.errors);
    }
  }

  onGridReady({ api }: GridReadyEvent): void {
    this.gridAPI = api;
    this.setBottomData();
    setTimeout(() => {
      this.gridAPI.sizeColumnsToFit();
    }, 200);
  }

  @HostListener('window:resize', ['$event'])
  onWindowResize(): void {
    this.gridAPI?.sizeColumnsToFit();
  }

  async openMapActivitiesModal(lineItemsType?: 'integration' | 'pdf'): Promise<void> {
    const ref = this.overlayService.openPopup<
      MapActivitiesModalParams,
      MapActivitiesModalReturnData,
      MapActivitiesModalComponent
    >({
      content: MapActivitiesModalComponent,
      data: {
        attributes: this.attributes(),
        budgetActivityOptions: this.budgetActivityOptions(),
        invoice: this.invoice(),
        items: this.gridData,
        lastUpdated: this.lastUpdated(),
        mappingLineItemsType: lineItemsType || null,
        noteId: this.noteId,
        noteMessage: this.noteControl.value || '',
      },
      settings: {
        header: 'Map Activities',
        primaryButton: {
          action: (instance) => instance?.save(),
          disabled: (instance) => !!instance?.isSaveButtonDisabled(),
          pendoTag: 'map-activities-save-button',
          tooltip: (instance) => instance?.saveButtonTooltip() || '',
        },
      },
    });

    const { data } = await firstValueFrom(ref.afterClosed$);

    if (data) {
      this.saveActivities(data);
    }
  }

  saveActivities(data: MapActivitiesModalReturnData): void {
    if (data.itemsToSave && data.itemsToSave?.length > 0) {
      this.activitiesSaving.set(true);
      this.gqlService
        .batchModifyInvoiceActivityMappings$({
          invoice_activity_mappings: data.itemsToSave,
          transaction_id: Utils.uuid(),
        })
        .subscribe((result) => {
          if (result.success) {
            this.implementActivitiesData(result.data || []);
            this.activitiesSaving.set(false);
          }
        });
    }

    if (data.isNotesChanged) {
      this.notesSaving.set(true);

      if (this.noteId) {
        if (data.notesMessage) {
          this.gqlService
            .updateNote$({
              id: this.noteId,
              message: data.notesMessage || '',
            })
            .subscribe((result) => {
              if (result.success) {
                this.getNotes();
                this.notesSaving.set(false);
              }
            });
        } else {
          this.gqlService.removeNote$(this.noteId).subscribe((result) => {
            if (result.success) {
              this.noteId = '';
              this.getNotes();
              this.notesSaving.set(false);
            }
          });
        }
      } else {
        this.gqlService
          .createNote$({
            entity_id: this.invoice().id || '',
            entity_type: EntityType.INVOICE_ACTIVITY_MAPPING,
            message: data.notesMessage || '',
            note_type: NoteType.NOTE_TYPE_GENERAL,
          })
          .subscribe((result) => {
            if (result.success) {
              this.getNotes();
              this.notesSaving.set(false);
            }
          });
      }
    }
  }

  private setBottomData(): void {
    const totals = this.calculatePinnedBottomData();

    this.gridAPI.setGridOption('pinnedBottomRowData', [
      {
        invoice_item_description: 'Total',
        ...totals,
      },
    ]);
  }
}
